import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { classStoreData } from 'src/operations/store';

const appLinks = {
  rise: {
    android: 'https://play.google.com/store/apps/details?id=com.culpmobile.rise',
    ios: 'https://apps.apple.com/kr/app/i-qsd/id6742775111',
  },
  creo: {
    android: 'https://play.google.com/store/apps/details?id=com.culpmobile.creo',
    ios: 'https://apps.apple.com/app/creo-bot/id6446823781',
  },
  educo: {
    android: 'https://play.google.com/store/apps/details?id=com.culpmobile.educo',
    ios: 'https://apps.apple.com/us/app/wawa-educo/id6742777145',
  },
  default: {
    android: 'https://play.google.com/store/apps/details?id=com.culpmobile',
    ios: 'https://apps.apple.com/app/class-boom/id6446192346',
  },
};

const toUniversalLink = (appUrl) => {
  if (typeof appUrl !== 'string' || !appUrl.includes('://')) {
    return appUrl;
  }

  try {
    const parsed = new URL(appUrl);
    const universalPath = `/${parsed.host}${parsed.pathname}login`;
    return `${window.location.origin}${universalPath}${parsed.search}${parsed.hash}`;
  } catch (e) {
    return appUrl;
  }
};

function OpenAppModal({ url, handleClose, visible, forceScheme = false }) {
  const defaultClass = useSelector(classStoreData);
  const [launchFailed, setLaunchFailed] = useState(false);
  const classSuffix = typeof defaultClass === 'string' ? defaultClass.toLowerCase() : '';
  const linkType = appLinks[classSuffix] ? classSuffix : 'default';
  const androidStoreLink = appLinks[linkType].android;
  const iosStoreLink = appLinks[linkType].ios;

  useEffect(() => {
    if (!visible || !url) return undefined;

    const isIOS =
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const universalUrl = isIOS && !forceScheme ? toUniversalLink(url) : url;
    const openUrl = universalUrl;
    console.log(openUrl);

    // iOS permission/app-switch prompt can take longer than Android.
    const fallbackDelay = isIOS ? 4000 : 1800;
    let switchedToApp = false;
    let fallbackTimer;
    let schemeFallbackTimer;
    setLaunchFailed(false);

    const markSwitchedToApp = () => {
      switchedToApp = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      handleClose();
    };

    const onVisibilityChange = () => {
      if (document.hidden) markSwitchedToApp();
    };
    const onPageHide = () => markSwitchedToApp();

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    window.location.href = openUrl;

    // Safety net: if universal link is not configured yet, retry with custom scheme.
    if (isIOS && universalUrl !== url) {
      schemeFallbackTimer = setTimeout(() => {
        if (!switchedToApp) {
          window.location.href = url;
        }
      }, 1200);
    }

    fallbackTimer = setTimeout(() => {
      if (!switchedToApp) {
        setLaunchFailed(true);
      }
    }, fallbackDelay);

    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      if (schemeFallbackTimer) {
        clearTimeout(schemeFallbackTimer);
      }
    };
  }, [visible, url, handleClose]);

  useEffect(() => {
    if (!visible || !launchFailed) return;

    const isIOS =
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) ||
      (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
    const storeLink = isIOS ? iosStoreLink : androidStoreLink;
    const message = isIOS ? 'iOS: App is not installed. Go to App Store now?' : 'Android: App is not installed. Go to Play Store now?';

    handleClose();
    setTimeout(() => {
      if (window.confirm(message)) {
        window.location.href = storeLink;
      }
    }, 0);
  }, [visible, launchFailed, iosStoreLink, androidStoreLink, handleClose]);

  return null;
}

export default OpenAppModal;
